import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Upload, Table, Select, Steps, message, Icon, Button } from "antd";
import * as XLSX from 'xlsx/dist/xlsx.full.min.js';

import { batch } from "../actions";

const { Step } = Steps;

const { Dragger } = Upload;
const { Option } = Select;

const systemIndex = {
    'Nome': 'name',
    'E-mail': 'email',
    'Telefone': 'phone',
    'Instagram': 'instagram',
    'Nome da Empresa': 'company_name',
    'CNPJ': 'cnpj',
    'Funcionários': 'employees',
    'Cidade': 'city',
    'Estado': 'state',
};

const ImportModal = ({ visible, onCancel }) => {
    const dispatch = useDispatch();

    const [fileList, setFileList] = useState([]);
    const [data, setData] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [mapping, setMapping] = useState({});
    const [step, setStep] = useState(0);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [failedLeads, setFailedLeads] = useState([]);

    function swapKeyValue(mapping) {
        const swapped = {};
        for (const [key, value] of Object.entries(mapping)) {
            swapped[value] = key;
        }
        return swapped;
    }

    const applyMapping = (rawData, mappingToApply) => {
        const mappingSwapped = swapKeyValue(mappingToApply);

        return rawData.map((row) => {
            return Object.keys(row).reduce((acc, fieldKey) => {
                if (mappingSwapped[fieldKey] === undefined) {
                    acc[fieldKey] = row[fieldKey];
                    return acc;
                }

                acc[systemIndex[mappingSwapped[fieldKey]]] = row[fieldKey];
                return acc;
            }, {});
        });
    };

    const handleUpload = ({ file }) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const workbook = XLSX.read(e.target.result, { type: "binary" });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const json = XLSX.utils.sheet_to_json(sheet);
            const excelColumns = json[0] ? Object.keys(json[0]) : [];
            setColumns(excelColumns);
            setOriginalData(json);

            const autoMapping = {};
            Object.keys(systemIndex).forEach((systemKey) => {
                if (excelColumns.includes(systemKey)) {
                    autoMapping[systemKey] = systemKey;
                }
            });
            setMapping(autoMapping);

            const transformedData = Object.keys(autoMapping).length > 0
                ? applyMapping(json, autoMapping)
                : json;
            setData(transformedData);

            setStep(1);
        };
        reader.readAsArrayBuffer(file);
        setFileList([file]);
    };

    const handleMappingChange = (value, key) => {
        const updatedMapping = { ...mapping, [key]: value };
        setMapping(updatedMapping);

        const updatedData = applyMapping(originalData, updatedMapping);
        setData(updatedData);
    };

    const handleSubmit = () => {
        setLoading(true);

        const updatedData = data.map((lead) => {
            const observation = Object.keys(lead)
                .filter(key => !Object.values(systemIndex).includes(key))
                .map(key => `${key}: ${lead[key]}`)
                .join('\n');

            return {
                ...lead,
                observation,
                entity_type: 'prospecting',
                column_id: 1,
                board_id: 1,
            };
        });

        dispatch(batch({ leads: updatedData }))
            .then((response) => {
                console.log('Import Response:', response);
                setLoading(false);
                const responseData = response && response.payload && response.payload.data && response.payload.data.data;
                console.log('Response Data:', responseData);
                const created = responseData && responseData.created;
                const failed = responseData && responseData.failed;
                const createdCount = created ? created.length : 0;
                const failedCount = failed ? failed.length : 0;

                if (createdCount > 0 && failedCount === 0) {
                    message.success(`${createdCount} lead(s) importado(s) com sucesso!`, 5);
                } else if (createdCount > 0 && failedCount > 0) {
                    message.warning(`${createdCount} importado(s), ${failedCount} não importado(s). Clique para ver detalhes.`, 8);
                    setFailedLeads(failed || []);

                    if (failed && failed.length > 0) {
                        console.group('❌ Leads que falharam na importação:');
                        failed.forEach((fail) => {
                            console.log(`• ${fail.data.name}: ${fail.reason}`);
                        });
                        console.groupEnd();
                    }
                } else if (failedCount > 0) {
                    const firstFailReason = failed && failed.length > 0 ? failed[0].reason : 'Erro desconhecido';
                    const isSameReason = failed && failed.every(f => f.reason === firstFailReason);

                    if (isSameReason && failedCount <= 3) {
                        message.error(`${failedCount} lead(s) não importado(s): ${firstFailReason}`, 8);
                    } else if (isSameReason) {
                        message.error(`${failedCount} lead(s) não importado(s): ${firstFailReason}`, 8);
                    } else {
                        message.error(`${failedCount} lead(s) não importado(s). Clique para ver detalhes.`, 8);
                    }

                    setFailedLeads(failed || []);

                    if (failed && failed.length > 0) {
                        console.group('❌ Leads que falharam na importação:');
                        failed.forEach((fail) => {
                            console.log(`• ${fail.data.name}: ${fail.reason}`);
                        });
                        console.groupEnd();
                    }
                }

                setStep(0);
                setFileList([]);
                setData([]);
                setOriginalData([]);
                setMapping({});
                onCancel();
            })
            .catch((error) => {
                console.error('Import Error (catch block):', error);
                message.error('Erro ao importar leads');
                setLoading(false);
            });
    };

    return (
        <>
        <Modal
            visible={visible}
            title="Importar leads"
            onCancel={onCancel}
            onOk={step === 2 ? handleSubmit : () => {
                if(data && data.length) {
                    setStep(step + 1);
                } else {
                    message.warning('Selecione um arquivo para continuar');
                }
            }}
            okText={step === 2 ? 'Importar' : 'Próximo'}
            confirmLoading={loading}
            width={900}
        >
            <Steps current={step} style={{ marginBottom: 16 }}>
                <Step title="Upload" />
                <Step title="Identificar Colunas" />
                <Step title="Confirmar" />
            </Steps>
            {step === 0 && (
                <Dragger
                    beforeUpload={() => false}
                    onChange={handleUpload}
                    fileList={fileList}
                >
                    <p className="ant-upload-drag-icon">
                        <Icon type="inbox" />
                    </p>
                    <p className="ant-upload-text">Clique ou arraste o arquivo para esta área para fazer o upload</p>
                </Dragger>
            )}
            {step === 1 && (
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                    {Object.keys(systemIndex).map((key, indexSys) => (
                        <div key={key} style={{ marginBottom: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <span style={{ marginRight: 8, textAlign: 'right', width: 150 }}>{key}: </span>
                            <Select
                                style={{ width: 200 }}
                                onChange={(value) => handleMappingChange(value, key)}
                                placeholder="Selecione uma coluna"
                                value={mapping[key]}
                            >
                                {columns.map((col) => (
                                    <Option key={col} value={col}>
                                        {col}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    ))}
                </div>
            )}
            {step === 2 && (
                <>
                    <Table 
                        dataSource={data} 
                        columns={Object.keys(systemIndex).map(key => ({
                            title: key,
                            dataIndex: systemIndex[key],
                            key: systemIndex[key],
                            width: 150, // Set a width for each column
                            ellipsis: true, // Add ellipsis to prevent row breaks
                        }))}
                        rowKey={(record, index) => index} 
                        pagination={true}
                        scroll={{ x: '100%' }} // Enable horizontal scrolling
                        style={{ width: '100%' }} // Set table width to 100%
                    />
                </>
            )}
        </Modal>

        <Modal
            title="Detalhes dos Erros de Importação"
            visible={failedLeads.length > 0}
            onCancel={() => setFailedLeads([])}
            footer={[
                <Button key="close" type="primary" onClick={() => setFailedLeads([])}>
                    Fechar
                </Button>
            ]}
            width={700}
        >
            <p style={{ marginBottom: 16 }}>
                Os seguintes leads não foram importados:
            </p>
            <Table
                dataSource={failedLeads}
                columns={[
                    {
                        title: 'Nome',
                        dataIndex: ['data', 'name'],
                        key: 'name',
                        width: 200,
                    },
                    {
                        title: 'Motivo',
                        dataIndex: 'reason',
                        key: 'reason',
                    }
                ]}
                rowKey={(record) => record.index}
                pagination={false}
                size="small"
            />
        </Modal>
        </>
    );
};

export default ImportModal;